<script setup lang="ts">
//#region import
import { ref, watch, nextTick } from 'vue';
import { isEmpty, merge } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePopupManager } from '@/components/PopupManager.vue';
import updateUrl from '@/service/updateUrl';
import { useLs } from '@/setup/vueLs';
import { useSessionAction } from '@/composables/useSessionAction';
import { useApi } from '@/api';
import { useTableSort } from '@/composables/useTableLS';
import type { 
  GetControlTowerCountriesTagsData, 
  GetControlTowerCountriesTagsResponse, 
} from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
//#endregion

//#region const
const fields: UiTableNewField<GetControlTowerCountriesTagsResponse['payload'][number]>[] = [
  {
    name: 'name',
    minWidth: '192',
    width: '192',
    align: 'left',
    resizable: true,
    sortable: 'custom',
  },
  {
    name: 'country_list',
    align: 'left',
    resizable: false,
    renderValue: (_, row) => row.values.join(', '),
  },
];
//#endregion


//#region composables
const route = useRoute();
const api = useApi();
const { t } = useI18n();
const { onSessionUnlock } = useSessionAction();
const LS_PATH = 'OffersCountryGroups/List';
const popupManager = usePopupManager();
//#endregion

//#region state
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<GetControlTowerCountriesTagsResponse['payload']>([]);
// const options = ref<NonNullable<GetControlTowerCountriesTagsData['query']>>({
//   sort_column: undefined,
//   sort_dir: undefined,
// });

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region watch
// watch(sortProp, (newValue) => {
//   optionsSort.value.sort_column = sortProp.value;
//   if (
//     fields.some(
//       (item) => item.name === optionsSort.value.sort_column && item.sortable === 'custom',
//     )
//   ) {
//     ls.set(`${LS_PATH}/sort_column`, newValue || 'name');
//   }
// });
// watch(sortOrder, (newValue) => {
//   optionsSort.value.sort_dir = sortOrder.value;
//   ls.set(`${LS_PATH}/sort_dir`, newValue || 'asc');
// });
//#endregion

//#region methods
const _query = () => {
  return optionsSort.value as GetControlTowerCountriesTagsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = _query();

  updateUrl.updateGetParams(_dataFilters);
};

const fetchData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  _completedFilterUrl();
  const options = _query();

  api.getCountriesTags(options).then((res) => {
    tableData.value = res.data.payload;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

const initData = () => {
  const _urlData = updateUrl.getParseParams(route.hash);
  if (!isEmpty(_urlData)) {
    merge(optionsSort.value, _urlData);
    setSort(_urlData);
  }
  nextTick(() => {
    fetchData();
  });
};

const handleAddCountryGroupPopup = () => {
  popupManager.addCountryGroup.open({
    listeners: {
      success: () => {
        initData();
      },
    },
  });
};

const handleEditCountryGroupPopup = (row: GetControlTowerCountriesTagsResponse['payload'][number]) => {
  const { name, id, values } = row;
  popupManager.addCountryGroup.open({
    data: {
      isEdit: true,
      popupData: {
        name,
        id,
        country_code: values,
      },
    },
    listeners: {
      success: () => {
        initData();
      },
    },
  });
};

const handleDelete = (row: GetControlTowerCountriesTagsResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.offersCountryGroups.confirm.deleteOffersCountryGroup'),
      message: t('settings.offersCountryGroups.confirm.confirmDelete', { name: row.name }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteCountriesTags(row.id);
          initData();
          context.handleClose();
        } catch (error) {
          window.console.error(error);
        }
      },
    },
  });
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

//#endregion

//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion
</script>

<template>
  <div class="offers-country-groups">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.offersCountryGroups.title') }}
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          @click="handleAddCountryGroupPopup"
        >
          {{ $t('settings.offersCountryGroups.addCountryGroup') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      lazy-loading
      i18n-path="settings.offersCountryGroups"
      is-static
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>

          <template v-slot="scope">
            <ActionIcon
              :tooltip="$t('crm.buttons.edit')"
              icon="pen"
              @click="handleEditCountryGroupPopup(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.offers-country-groups {

}
</style>
